import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useUser } from '../../hooks/useUser';

export interface StatBoardRow {
  id: string;
  name: React.ReactNode;
  value: React.ReactNode;
  /** Comparable value for tie-aware ranking. Falls back to `value` when it's a string or number. */
  rawValue?: number | string;
  /** Small line under the name (e.g. "W 34 / D 5 / L 12"). */
  sub?: React.ReactNode;
  /** Link target; defaults to the player's profile. Pass null for none. */
  to?: string | null;
}

const MEDAL = ['text-[#e8c15a]', 'text-[#c7ccd6]', 'text-[#d08a4e]'];

/** Olympic-style rank: 1 + number of rows with a strictly higher value. Rows without a comparable value rank by position. */
export function tieRanks(rows: Pick<StatBoardRow, 'value' | 'rawValue'>[]): number[] {
  const comparable = (r: Pick<StatBoardRow, 'value' | 'rawValue'>) =>
    r.rawValue !== undefined ? r.rawValue : typeof r.value === 'string' || typeof r.value === 'number' ? r.value : undefined;
  return rows.map((r, i) => {
    const v = comparable(r);
    if (v === undefined) return i + 1;
    return 1 + rows.filter((o, j) => j !== i && (comparable(o) as number | string | undefined) !== undefined && (comparable(o) as number | string) > v).length;
  });
}

/**
 * UT ranked list panel used by the leaderboard STATS tab (and anywhere the
 * classic AwardCard / StatsCard renders under the redesign). Top three ranks
 * get gold / silver / bronze numbers; the signed-in player's row is tinted.
 */
export const UtStatBoard: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  rows: StatBoardRow[];
  valueHeader?: string;
  empty?: React.ReactNode;
  className?: string;
}> = ({ title, description, rows, valueHeader, empty = 'No data yet', className }) => {
  const { player } = useUser();
  const ranks = tieRanks(rows);
  return (
    <div className={clsx('flex flex-col gap-3 rounded-[14px] border border-ut-line bg-ut-card p-3.5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="font-ut-display text-xl font-extrabold uppercase leading-tight">{title}</span>
          {description && <span className="text-xs text-ut-mut">{description}</span>}
        </div>
        {valueHeader && <span className="pt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-ut-mut">{valueHeader}</span>}
      </div>
      {rows.length === 0 ? (
        <span className="text-sm text-ut-mut">{empty}</span>
      ) : (
        <div className="flex flex-col">
          {rows.map((r, i) => {
            const rank = ranks[i];
            const me = player?.id === r.id;
            const to = r.to === undefined ? `/players/${r.id}` : r.to;
            const inner = (
              <>
                <span className={clsx('w-6 flex-none font-ut-display text-base font-extrabold', rank <= 3 ? MEDAL[rank - 1] : 'text-ut-mut')}>{rank}</span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-semibold leading-snug">{r.name}</span>
                  {r.sub && <span className="text-[11px] text-ut-mut">{r.sub}</span>}
                </span>
                <b className="flex-none whitespace-nowrap text-right font-ut-display text-base font-extrabold">{r.value}</b>
              </>
            );
            const cls = clsx('flex items-center gap-2 border-t border-ut-line px-1.5 py-2 first:border-t-0', me && '-mx-1.5 rounded-lg bg-ut-acc-soft px-3');
            return to ? (
              <Link key={`${r.id}-${i}`} to={to} className={clsx(cls, 'transition-colors hover:text-ut-acc')}>
                {inner}
              </Link>
            ) : (
              <div key={`${r.id}-${i}`} className={cls}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
